import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, CreditCard } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { openPortal, startCheckout } from "@/lib/billing";
import { gbp, longDate } from "@/lib/format";
import { planById } from "@/lib/plans";
import { AgentLayout } from "@/components/agent/AgentLayout";
import { SettingsCard } from "@/components/ui/settings-card";
import { EmptyState, Loading, PageHeader, PrimaryButton, ProgressBar, SecondaryButton } from "@/components/agent/ui";
const pounds = (pence) => gbp(pence / 100, pence % 100 ? 2 : 0);
/** Fetches the plan and this month's screenings. */
export const useBilling = () => {
  const [billing, setBilling] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    try {
      setBilling((await api.get("/billing")).data);
      setError("");
    } catch (err) {
      setError(errorText(err));
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  return { billing, error, load };
};
const Stat = ({ label, value, testid }) => (
  <div className="min-w-0">
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="mt-0.5 text-sm font-medium tabular-nums" data-testid={testid}>{value}</p>
  </div>
);
const YourPlan = ({ b }) => {
  const plan = planById(b.plan);
  const top = b.plan === "pro";
  return (
    <SettingsCard title="Your plan" id="plan" testid="billing-plan">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="min-w-0">
          <p className="text-lg font-semibold" data-testid="billing-plan-name">{b.plan_name}</p>
          <p className="text-sm text-muted-foreground" data-testid="billing-plan-price">
            {b.monthly_price ? `${pounds(b.monthly_price)} a month` : `${pounds(b.per_screening)} per screening, no monthly fee`}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">For agencies letting about {plan.fits}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {b.can_change && b.has_customer && <SecondaryButton onClick={() => openPortal(b.plan)} testid="billing-manage">Manage payment</SecondaryButton>}
          {b.can_change && b.plan === "payg" && !b.has_subscription && <SecondaryButton onClick={() => startCheckout("payg")} testid="billing-add-card"><CreditCard /> Add a card</SecondaryButton>}
          {top
            ? <SecondaryButton to="/billing/plans" testid="billing-see-plans">See plans</SecondaryButton>
            : <PrimaryButton to="/billing/plans" testid="billing-upgrade"><ArrowUpRight /> Upgrade</PrimaryButton>}
        </div>
      </div>
      {b.plan === "payg" && !b.has_subscription && (
        <p className="text-xs text-muted-foreground" data-testid="billing-no-card">Add a card to keep screening on pay as you go. You're charged {pounds(b.per_screening)} per screening at the end of each month.</p>
      )}
    </SettingsCard>
  );
};
const Usage = ({ b }) => {
  const allowance = b.included + b.rollover;
  return (
    <SettingsCard title="Usage this month" description={`${longDate(b.period_start)} to the end of the month. Resets on ${longDate(b.period_end)}.`} id="usage" testid="billing-usage">
      {b.included ? (
        <>
          <div className="flex items-baseline justify-between gap-3">
            <p className="text-sm"><span className="text-2xl font-semibold tabular-nums" data-testid="billing-used">{b.used}</span>
              <span className="text-muted-foreground"> of {allowance} screenings used</span></p>
            <span className="text-sm font-medium tabular-nums" data-testid="billing-remaining">{b.remaining} left</span>
          </div>
          <ProgressBar value={Math.min(b.used, allowance)} total={allowance} />
          <div className="grid grid-cols-2 gap-4 border-t pt-4 sm:grid-cols-3">
            <Stat label="Included each month" value={b.included} />
            <Stat label="Rolled over from last month" value={b.rollover} testid="billing-rollover" />
            <Stat label="Unused screenings" value="Roll over for 1 month" />
          </div>
        </>
      ) : (
        <>
          <p className="text-sm"><span className="text-2xl font-semibold tabular-nums" data-testid="billing-used">{b.used}</span>
            <span className="text-muted-foreground"> {b.used === 1 ? "screening" : "screenings"} so far</span></p>
          <div className="grid grid-cols-2 gap-4 border-t pt-4">
            <Stat label="Charges so far" value={pounds(b.charges_so_far)} testid="billing-charges" />
            <Stat label="Price per screening" value={pounds(b.per_screening)} />
          </div>
        </>
      )}
      <p className="text-xs text-muted-foreground">A screening is one application a tenant sends you.</p>
    </SettingsCard>
  );
};
/** Back from Stripe Checkout with ?session_id: wait for Stripe to confirm, then show the new plan. */
const useCheckoutReturn = (load) => {
  const [params, setParams] = useSearchParams();
  const sessionId = params.get("session_id");
  useEffect(() => {
    if (!sessionId) return undefined;
    let tries = 0;
    let stop = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/billing/checkout/${sessionId}`);
        if (data.payment_status === "paid") {
          toast.success(`You're now on ${planById(data.plan).name}.`);
        } else if (data.status === "expired" || ++tries >= 10) {
          toast.error(data.status === "expired" ? "The payment wasn't completed, so your plan hasn't changed." : "We're still waiting for Stripe to confirm. Refresh in a minute.");
        } else {
          if (!stop) setTimeout(poll, 2000);
          return;
        }
      } catch (err) {
        toast.error(errorText(err));
      }
      setParams({}, { replace: true });
      load();
    };
    poll();
    return () => { stop = true; };
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps
  return Boolean(sessionId);
};

export default function Billing() {
  const { billing, error, load } = useBilling();
  const confirming = useCheckoutReturn(load);
  let body;
  if (error) body = <EmptyState title="Couldn't load billing" text={error} action={<SecondaryButton onClick={load} testid="billing-retry">Try again</SecondaryButton>} testid="billing-error" />;
  else if (!billing || confirming) body = <Loading testid="billing-loading" />;
  else body = <><YourPlan b={billing} /><Usage b={billing} /></>;
  return (
    <AgentLayout>
      <PageHeader title="Billing" description="Your plan and how many screenings you've used." />
      {body}
    </AgentLayout>
  );
}
